import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, IconButton, Typography } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import CalibrationGrid from "../MicroTip/CalibrationData";
import PropertiesForm from "../MicroTip/Parameters";
import Header from "../MicroTip/Header";
import History from "../MicroTip/History";
import Splines from "../MicroTip/Splines";
import { getInitialState } from "./DefaultParameters";

export default function CollapsibleVerticalLayout() {
    // ------------------------------------------------------------
    // Main states
    // ------------------------------------------------------------
    const [isResultsCollapsed, setResultsCollapsed] = useState(true);
    const [isCalibrationCollapsed, setCalibrationCollapsed] = useState(false);
    const [isParametersCollapsed, setParametersCollapsed] = useState(false);
    const [isSplineCollapsed, setSplineCollapsed] = useState(false);

    const [selectedModel, setSelectedModel] = useState("0");
   
    const [
        splines,
        setSplines
    ] = useState({       
        g1: null,
        g2: null
    });

    const [rows, setRows] = useState(
        Array.from({ length: 5 }, () => ({
            conc: "",
            resp: ""
        }))
    );

    const [parameters, setParameters] = useState(getInitialState());

    // ------------------------------------------------------------
    // Calibration history
    // ------------------------------------------------------------
    const [resultDataMap, setResultDataMap] = useState(() => {
        const saved = sessionStorage.getItem("calibrationHistory");

        try {
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.error("Failed to parse calibrationHistory:", error);
            return {};
        }
    });

    

    // ------------------------------------------------------------
    // Results scroll reference
    // ------------------------------------------------------------
    const resultsRef = useRef(null);

    // ------------------------------------------------------------
    // Expand Results when result data is available
    // ------------------------------------------------------------
    useEffect(() => {
        const hasData =
            resultDataMap &&
            Object.keys(resultDataMap).length > 0;

        if (hasData) {
            setResultsCollapsed(false);
        }
    }, [resultDataMap]);

    // ------------------------------------------------------------
    // Debug parameter changes
    // ------------------------------------------------------------
    useEffect(() => {
        console.log(
            "TXBVersion =",
            parameters?.TXBVersion
        );
    }, [parameters]);

    // ------------------------------------------------------------
    // Persist spline data
    // ------------------------------------------------------------
    useEffect(() => {
        try {
            localStorage.setItem(
                "microtipCalibrationSplines",
                JSON.stringify(splines || {})
            );
        } catch (error) {
            console.error(
                "Failed to save spline data:",
                error
            );
        }
    }, [splines]);

    return (
        <div>
            {/* ============================================================
                HEADER
            ============================================================ */}
            <Header
                rows={rows}
                parameters={parameters}
                setParameters={setParameters}
                setResultDataMap={setResultDataMap}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                splines={splines}
                onCalibrate={() => {
                    setCalibrationCollapsed(true);
                    setParametersCollapsed(true);
                    setResultsCollapsed(false);

                    setTimeout(() => {
                        resultsRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start"
                        });
                    }, 150);
                }}
            />

            <Box
                display="flex"
                flexDirection="column"
                padding={2}
            >

                {/* ========================================================
                    SPLINES
                ======================================================== */}
                {String(parameters?.TXBVersion) === "4" && (
                    <Paper
                        elevation={3}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 0
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            bgcolor="#774def"
                            color="#ffffff"
                            px={2}
                            py={1}
                        >
                            <Typography variant="subtitle1">
                                Splines
                            </Typography>

                            <IconButton
                                size="small"
                                onClick={() =>
                                    setSplineCollapsed(
                                        !isSplineCollapsed
                                    )
                                }
                                sx={{ color: "white" }}
                            >
                                {isSplineCollapsed ? (
                                    <ExpandMore />
                                ) : (
                                    <ExpandLess />
                                )}
                            </IconButton>
                        </Box>

                        <Box
                            p={2}
                            sx={{
                                display: isSplineCollapsed
                                    ? "none"
                                    : "block"
                            }}
                        >
                            <Splines
                                splines={splines}
                                setSplines={setSplines}
                            />
                        </Box>
                    </Paper>
                )}

                {/* ========================================================
                    CALIBRATION DATA
                ======================================================== */}
                <Paper
                    elevation={3}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        mt: 2
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor="#774def"
                        color="#ffffff"
                        px={2}
                        py={1}
                    >
                        <Typography variant="subtitle1">
                            Calibration Data
                        </Typography>

                        <IconButton
                            size="small"
                            onClick={() =>
                                setCalibrationCollapsed(
                                    !isCalibrationCollapsed
                                )
                            }
                            sx={{ color: "white" }}
                        >
                            {isCalibrationCollapsed ? (
                                <ExpandMore />
                            ) : (
                                <ExpandLess />
                            )}
                        </IconButton>
                    </Box>

                    <Box
                        flex={1}
                        overflow="auto"
                        p={2}
                        sx={{
                            display: isCalibrationCollapsed
                                ? "none"
                                : "block"
                        }}
                    >
                        <CalibrationGrid
                            rows={rows}
                            setRows={setRows}
                        />
                    </Box>
                </Paper>

                {/* ========================================================
                    PARAMETERS
                ======================================================== */}
                <Paper
                    elevation={3}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        mt: 2
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor="#774def"
                        color="#ffffff"
                        px={2}
                        py={1}
                    >
                        <Typography variant="subtitle1">
                            Parameters
                        </Typography>

                        <IconButton
                            size="small"
                            onClick={() =>
                                setParametersCollapsed(
                                    !isParametersCollapsed
                                )
                            }
                            sx={{ color: "white" }}
                        >
                            {isParametersCollapsed ? (
                                <ExpandMore />
                            ) : (
                                <ExpandLess />
                            )}
                        </IconButton>
                    </Box>

                    <Box
                        flex={1}
                        overflow="auto"
                        p={2}
                        sx={{
                            display: isParametersCollapsed
                                ? "none"
                                : "block"
                        }}
                    >
                        <PropertiesForm
                            parameters={parameters}
                            setParameters={setParameters}
                        />
                    </Box>
                </Paper>

                {/* ========================================================
                    RESULTS
                ======================================================== */}
                <Paper
                    ref={resultsRef}
                    elevation={3}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        mt: 2
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor="#774def"
                        color="#ffffff"
                        px={2}
                        py={1}
                    >
                        <Typography variant="subtitle1">
                            Results
                        </Typography>

                        <IconButton
                            size="small"
                            onClick={() =>
                                setResultsCollapsed(
                                    !isResultsCollapsed
                                )
                            }
                            sx={{ color: "white" }}
                        >
                            {isResultsCollapsed ? (
                                <ExpandMore />
                            ) : (
                                <ExpandLess />
                            )}
                        </IconButton>
                    </Box>

                    <Box
                        flex={1}
                        overflow="auto"
                        p={2}
                        sx={{
                            display: isResultsCollapsed
                                ? "none"
                                : "block"
                        }}
                    >
                        <History
                            resultDataMap={resultDataMap}
                            setResultDataMap={setResultDataMap}
                            rows={rows}
                            parameters={parameters}
                        />
                    </Box>
                </Paper>
            </Box>
        </div>
    );
}